import React, { useState, useCallback, useEffect } from 'react';
import './App.css';

// Context
import { WebSocketProvider } from './contexts/WebSocketContext';

// Components
import { StatusDisplay, CallState } from './components/StatusDisplay';
import { ErrorDisplay } from './components/ErrorDisplay';
import { ConversationDisplay } from './components/ConversationDisplay';
import { CallInterface } from './components/CallInterface';
import TapToEnableAudio from './components/TapToEnableAudio';

// Hooks
import { useCallSession } from './hooks/useCallSession';
import { useAudioRecording, AppError } from './hooks/useAudioRecording';
import { useAudioPlayer } from './hooks/useAudioPlayer';
import { useWebSocketContext, ProcessingStage, VoiceInteractionResult } from './contexts/WebSocketContext';

const SILENT_AUDIO_B64 = 'UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';

function AppContent() {
  // State
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const [callState, setCallState] = useState<CallState>('idle');
  const [error, setError] = useState<AppError | null>(null);
  const [processingStage, setProcessingStage] = useState<ProcessingStage | null>(null);
  const [lastTranscription, setLastTranscription] = useState<string | null>(null);
  const [lastResponse, setLastResponse] = useState<string | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [showAudioPrompt, setShowAudioPrompt] = useState(isMobile);
  const [isUnlockingAudio, setIsUnlockingAudio] = useState(false);

  

  // Custom hooks
  const { timer, conversationId, setConversationId, formatTimer, startSession, endSession, isActive } = useCallSession();
  const { isRecording, isListening, error: recordingError, startRecording, stopRecording, updateCallbacks, setVADSensitivity } = useAudioRecording();
  const { isPlaying, playAudio, stopAudio, getCurrentAudioInfo, unlockAudioContext } = useAudioPlayer();
  const { isConnected, connect, processVoiceInteraction } = useWebSocketContext();

  // Derive voice status from VAD states
  const voiceStatus = isRecording ? 'recording' : isListening ? 'listening' : 'idle';
  
  // Calculate recording duration (simplified)
  const recordingDuration = 0;
  
  // Derive processing state
  const isProcessing = processingStage !== null;

  // Handle speech start for barge-in
  const handleSpeechStart = useCallback(() => {
    if (isPlaying) {
      console.log('Barge-in detected: stopping audio playback.');
      stopAudio();
    }
  }, [isPlaying, stopAudio]);

  // Handle voice interaction flow
  const handleVoiceInteraction = useCallback(async (audioData: string, mimeType: string) => {
    try {
      setProcessingStage('transcribing');
      stopAudio();
      const result = await processVoiceInteraction(
        audioData,
        mimeType,
        conversationId || '',
        (stage) => setProcessingStage(stage)
      );

      console.log('Voice interaction result:');
      console.dir(result);
      
      // Update state with results
      setLastTranscription(result.transcription.text);
      setLastResponse(result.chatResponse.response);
      
      // Play the audio response
      playAudio(result.speechResponse.audioData, result.speechResponse.audioFormat).catch(err => {
        console.error('Failed to play audio response:', err);
      });
      
      
      // Update call session with conversation ID
      if (!conversationId && result.chatResponse.conversationId) {
        setConversationId(prev => result.chatResponse.conversationId || prev || null);
      }
      
      setProcessingStage(null); // Reset to initial state
    } catch (error) {
      console.error('Voice interaction failed:', error);
      setError({
        message: (error as Error).message,
        type: 'unknown',
        retryable: true
      });
      setProcessingStage(null); // Reset to initial state
    }
  }, [processVoiceInteraction, playAudio, conversationId]);

  // Handle call actions
  const handleStartCall = useCallback(async () => {
    try {
      startSession();
      // Start VAD recording when call session begins
      await startRecording(handleVoiceInteraction, handleSpeechStart);
    } catch (error) {
      console.error('Failed to start call session:', error);
      setError({
        message: 'Failed to start recording. Please check your microphone permissions.',
        type: 'audio',
        retryable: true
      });
    }
  }, [startSession, startRecording, handleVoiceInteraction, handleSpeechStart]);

  const handleEndCall = useCallback(async () => {
    try {
      endSession();
      // Stop VAD recording when call session ends
      await stopRecording();
      stopAudio();
      // Update state with results
      setLastTranscription(null);
      setLastResponse(null);
    } catch (error) {
      console.error('Failed to stop recording:', error);
    }
  }, [endSession, stopRecording]);

  // Update call state based on voice status and processing
  useEffect(() => {
    const currentError = error || recordingError;
    
    if (currentError) {
      setCallState('error');
    } else if (isRecording) {
      setCallState('recording');
    } else if (isPlaying) {
      setCallState('playing');
    } else if (isProcessing) {
      setCallState('processing');
    } else {
      setCallState('idle');
    }
  }, [isRecording, isProcessing, error, recordingError, isPlaying]);

  // Handle retry
  const handleRetry = useCallback(async () => {
    setError(null);
    await connect();
  }, [connect]);

  // Clear errors when recording error changes
  useEffect(() => {
    if (!recordingError && error?.type === 'audio') {
      setError(null);
    }
  }, [recordingError, error]);

  // Update recording callback when handleVoiceInteraction changes
  useEffect(() => {
    if (isListening && updateCallbacks) {
      updateCallbacks(handleVoiceInteraction, handleSpeechStart);
    }
  }, [handleVoiceInteraction, isListening, updateCallbacks, handleSpeechStart]);

  // Show TapToEnableAudio if NotAllowedError or not enabled
  useEffect(() => {
    if (error && error.type === 'audio' && error.message.includes('NotAllowedError')) {
      setShowAudioPrompt(true);
    }
  }, [error]);

  const handleEnableAudio = useCallback(async () => {
    if (isUnlockingAudio) return; // Prevent multiple clicks
    setIsUnlockingAudio(true);

    try {
      await unlockAudioContext();
      setAudioEnabled(true);
      setShowAudioPrompt(false);
      console.log("Audio context unlocked successfully.");
    } catch (e) {
      console.error("Failed to unlock audio context:", e);
      setError({
        message: 'Could not enable audio. Please tap again to grant permission.',
        type: 'audio',
        retryable: false // This is not retryable via the button, user must tap the prompt again
      });
      setIsUnlockingAudio(false); // Allow user to try again
    }
  }, [isUnlockingAudio, unlockAudioContext]);

  // Adjust VAD sensitivity based on playback state for barge-in
  useEffect(() => {
    if (setVADSensitivity) {
      // When audio is playing, we want VAD to be less sensitive to avoid picking up
      // the playback as speech. However, it should still be sensitive enough to detect
      // the user speaking over the audio (barge-in).
      const isPlayback = isPlaying;
      console.log(`Adjusting VAD sensitivity. Playback active: ${isPlayback}`);
      setVADSensitivity(!isPlayback); // Be LESS sensitive during playback
    }
  }, [isPlaying, setVADSensitivity]);

  const currentError = error || recordingError;

  return (
    <div className="App min-h-screen flex flex-col relative overflow-hidden">
      {showAudioPrompt && (
        <TapToEnableAudio 
          onEnable={handleEnableAudio}
          isUnlocking={isUnlockingAudio}
        />
      )}
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-64 h-64 bg-qatar-maroon rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-48 h-48 bg-qatar-gold rounded-full blur-3xl"></div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-0 md:p-4 relative z-10">
        <div className="w-full max-w-md mx-auto">
          <div className="glass-container p-6 flex flex-col overflow-hidden">
            {/* Top Section - Status & Avatar */}
            <div className="flex-shrink-0">
              <StatusDisplay
                callState={callState}
                processingStage={processingStage}
                isConnected={isConnected}
                error={currentError}
                isSessionActive={isActive}
                timer={timer}
                formatTimer={formatTimer}
                isListening={isListening}
              />
            </div>

            {/* Middle Section - Error Display and Conversation (scrollable if needed) */}
            <div className="flex-1 flex flex-col justify-center space-y-4 overflow-y-auto min-h-0 conversation-area">
              <ErrorDisplay
                error={currentError}
                isConnected={isConnected}
                onRetry={handleRetry}
              />

              {isActive && (lastTranscription || lastResponse) && (
                <ConversationDisplay
                  lastTranscription={lastTranscription}
                  lastResponse={lastResponse}
                />
              )}
            </div>

            {/* Bottom Section - Call Controls */}
            <div className="flex-shrink-0 pt-4">
              <CallInterface
                isSessionActive={isActive}
                error={currentError}
                isConnected={isConnected}
                onStartCall={handleStartCall}
                onEndCall={handleEndCall}
              />
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <WebSocketProvider>
      <AppContent />
    </WebSocketProvider>
  );
}

export default App; 